import { gql } from '@apollo/client';
import { useSubscription } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';

import { DownloadFailedDocumentsJobContext } from './context';
import { useResettableMutation } from '../../hooks/useResettableMutation';

const START_JOB = gql`
  mutation startDownloadFailedDocumentsJob($notificationJobId: ID!) {
    downloadFailedDocuments(notificationJobId: $notificationJobId)
  }
`;

const ON_DOWNLOAD_FAILED_DOCUMENTS_JOB_PROGRESS = gql`
  subscription jobProgress($jobId: ID!) {
    job: downloadFailedDocumentsJobProgress(jobId: $jobId) {
      jobId
      filename
      url
      finished
      message
      errors
    }
  }
`;

export default function DownloadFailedDocumentsJobProvider({ children }) {
  const [jobId, setJobId] = useState(null);
  const [jobErrors, setJobErrors] = useState([]);
  const [finished, setJobFinished] = useState(true);
  const [message, setMessage] = useState(null);
  const [filename, setFilename] = useState(null);
  const [url, setUrl] = useState(null);

  const clearJobId = useCallback(() => {
    setJobId(null);
    setJobErrors([]);
  }, []);

  useEffect(() => {
    if (!!jobId && !!finished) {
      setTimeout(() => clearJobId(), 10000);
    }
  }, [finished, clearJobId, jobId]);

  const [startDownloadFailedDocsReportJob, { loading, error, reset }] = useResettableMutation(START_JOB, {
    onCompleted: data => {
      setJobFinished(false);
      setJobId(data?.downloadFailedDocuments);
    },
    onError: () => setTimeout(() => reset(), 10000)
  });

  const startDownloadFailedDocumentsJob = data => {
    setJobFinished(false);
    startDownloadFailedDocsReportJob({ variables: { notificationJobId: data?.notificationJobId } });
  };

  useSubscription(ON_DOWNLOAD_FAILED_DOCUMENTS_JOB_PROGRESS, {
    skip: !jobId,
    variables: { jobId },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const job = data?.job;
      const { message, filename, url, finished, errors } = job;

      errors && setJobErrors(errors);
      setJobFinished(finished);
      setMessage(message);
      setFilename(filename);
      setUrl(url);
    }
  });

  return (
    <DownloadFailedDocumentsJobContext.Provider
      value={{
        startDownloadFailedDocumentsJob,
        jobId,
        clearJobId,
        loading,
        error,
        finished,
        filename,
        url,
        message,
        jobErrors
      }}
    >
      {children}
    </DownloadFailedDocumentsJobContext.Provider>
  );
}
